import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import SydButton from '../../../../../commonDesign/Button'
import Markup from '../../../../../atoms/Markup'
import PlanStatusIcon from '../badges/PlanStatusIcon'
import { detectMarkupType, formatPlanDate } from '../helpers'
import usePanelMaxHeight from '../hooks/usePanelMaxHeight'
import EmbeddedItemSupplemental from './EmbeddedItemSupplemental'
import DetailRow from './DetailRow'

const useStyles = makeStyles(() => ({
  panel: {
    minHeight: '300px',
    borderRadius: 'var(--border-radius-lg)',
    background: 'var(--color-neutral-100)',
    border: 'var(--border-neutral-thin)',
    overflow: 'hidden'
  },
  panelTitle: {
    borderBottom: 'var(--border-neutral-thin)',
    padding: 'var(--dim-2) var(--dim-4)',
    color: 'var(--color-neutral-800)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  panelContent: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: 'var(--dim-4)'
  },
  detailList: {
    '& .__detail-item': {
      display: 'flex',
      gap: 'var(--dim-4)',
      padding: 'var(--dim-2) 0',
      alignItems: 'center'
    },
    '& .__detail-item:not(:last-child)': {
      borderBottom: 'var(--border-neutral-thin)'
    },
    '& .__detail-item-header': {
      width: '120px',
      color: 'var(--color-neutral-800)',
      fontWeight: 'var(--font-weight-light)',
      flexShrink: 0
    },
    '& .__detail-item-content': {
      flex: 1,
      minWidth: 0,
      wordBreak: 'break-word'
    }
  },
  attentionBox: {
    background: 'color-mix(in srgb, var(--color-decline-500) 10%, transparent)',
    padding: 'var(--dim-3)',
    borderRadius: 'var(--border-radius-md)'
  }
}))

const sectionLabelStyle = {
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
}

function PlanItemDetailPanel ({ item, category, statusStyleOverrides, taskComponent, onClose }) {
  const classes = useStyles()
  const { ref, maxHeight } = usePanelMaxHeight()

  return (
    <div ref={ref} className={`${classes.panel} flex flex-col`} style={{ maxHeight: maxHeight ?? undefined }}>
      <div className={`${classes.panelTitle} flex items-center justify-between font-size-xs`} style={{ flexShrink: 0 }}>
        <span>{category?.name || ''}</span>
        <SydButton size='xs' variant='secondary' onClick={onClose}>Close</SydButton>
      </div>

      <div className={`${classes.panelContent} flex flex-col gap-md`}>
        <div className='flex flex-col gap-2'>
          <PlanStatusIcon status={item.status} size={30} statusStyleOverrides={statusStyleOverrides} />
          <p className='font-size-lg font-medium m-0'>{item.name}</p>
          {item.description ? (
            <div className='font-size-sm font-light m-0' style={{ color: 'var(--text-color-subtle)' }}>
              <Markup value={item.description} type={detectMarkupType(item.description)} style={{ fontSize: 'inherit' }} />
            </div>
          ) : null}
        </div>

        <div className={`${classes.detailList} flex flex-col font-size-sm`}>
          <DetailRow label='Status' value={item.status} />
          <DetailRow label='Advisor Notes' value={item.metadata?.advisorNote} />
          <DetailRow label='Next Review Date' value={formatPlanDate(item.metadata?.nextReviewDate)} />
          <DetailRow label='Last Reviewed Date' value={formatPlanDate(item.metadata?.lastReviewedDate)} />
        </div>

        {item.metadata?.needsAttention ? (
          <div className={classes.attentionBox}>
            <div
              className='font-size-xs font-medium mb-1'
              style={{ color: 'var(--color-decline-500)', ...sectionLabelStyle }}
            >
              Needs Attention
            </div>
            {item.metadata.needsAttentionReason ? (
              <div className='font-size-sm'>{item.metadata.needsAttentionReason}</div>
            ) : null}
          </div>
        ) : null}

        {taskComponent?.blueprintCode && taskComponent?.presentationKey ? (
          <EmbeddedItemSupplemental item={item} taskComponent={taskComponent} />
        ) : null}
      </div>
    </div>
  )
}

PlanItemDetailPanel.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    status: PropTypes.string,
    description: PropTypes.string,
    metadata: PropTypes.object,
    taskIds: PropTypes.array,
    tasks: PropTypes.array
  }).isRequired,
  category: PropTypes.shape({
    name: PropTypes.string
  }),
  statusStyleOverrides: PropTypes.object,
  taskComponent: PropTypes.object,
  onClose: PropTypes.func.isRequired
}

export default PlanItemDetailPanel
